'use client';

import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { Shield } from "lucide-react";
import { Button } from '@/components/ui/button';
import { safeCallbackUrl } from "@/lib/safe-redirect";

/**
 * Admin login page — uses SEPARATE admin auth (not NextAuth).
 * 
 * Architecture: Admin uses its own JWT cookie (__admin-token),
 * completely independent from the NextAuth session.
 * This allows tenant and admin sessions to coexist.
 * 
 * Flow:
 * 1. POST /api/admin-auth/login with credentials
 * 2. API validates + checks role === 'system'
 * 3. API sets __admin-token cookie (separate from NextAuth)
 * 4. Redirect to admin dashboard
 */
function AdminLoginForm() {
    const searchParams = useSearchParams();
    const rawCallbackUrl = searchParams.get("callbackUrl");
    
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username || !password) {
            setError('Vui lòng nhập username và password');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const res = await fetch('/api/admin-auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });

            const data = await res.json();

            if (!res.ok) {
                if (res.status === 403) {
                    setError('Tài khoản không có quyền admin (cần role: system)');
                } else {
                    setError('Username hoặc password không đúng');
                }
                setLoading(false);
                return;
            }

            // Success — redirect to admin dashboard. The query string names the place, so only a path of the site or a url of
            // this origin is followed (`javascript:` and other sites are not; safe-redirect.ts). No "platform domain" rule:
            // NEXT_PUBLIC_* is fixed at build time and the production image has none (see login/page.tsx).
            window.location.href = safeCallbackUrl(rawCallbackUrl, "/", { origin: window.location.origin });
        } catch (err) {
            setError('Lỗi kết nối. Vui lòng thử lại.');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <div className="w-full max-w-md p-8">
                <div className="bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-gray-700/50">
                    {/* Icon */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
                            <Shield className="w-8 h-8 text-amber-400" />
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Admin Panel</h1>
                        <p className="text-gray-400 text-sm">System administrator access only</p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="admin"
                                autoFocus
                                className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                            />
                        </div>
                        
                        {error && (
                            <div className="text-red-400 text-sm text-center bg-red-900/20 border border-red-800/30 rounded-lg py-2">
                                {error}
                            </div>
                        )}
                        
                        <Button variant="ghost"  
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-amber-500 text-gray-900 font-semibold rounded-xl hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Đang xác thực...' : 'Đăng nhập Admin'}
                        </Button>
                    </form>

                    <p className="text-center text-xs text-gray-500 mt-6">
                        Requires system administrator credentials
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function AdminLoginPage() {
    return (
        <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-gray-900">
                <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
            </div>
        }>
            <AdminLoginForm />
        </Suspense>
    );
}
